import { useMemo, useState } from 'react';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import type { ScatterPlotCustomSvgLayerProps } from '@nivo/scatterplot';
import type { GeoBrowsePcaPoint, GeoBrowseSample } from '../../types';
import styles from '../../GeoBrowse.module.css';

const CHART_THEME = {
  axis: {
    ticks: { text: { fontSize: 11, fill: '#6b7280' } },
    legend: { text: { fontSize: 12, fill: '#374151' } },
  },
  grid: { line: { stroke: '#e5e7eb' } },
};

export type PcaColorBy = 'group' | 'sex' | 'tissue';

const COLOR_BY_LABELS: Record<PcaColorBy, string> = {
  group: 'sample_group',
  sex: 'sex',
  tissue: 'tissue',
};

const GROUP_COLORS: Record<string, string> = {
  case: '#5b9a8b',
  control: '#94a3b8',
};

const SEX_COLORS: Record<string, string> = {
  male: '#3b82f6',
  m: '#3b82f6',
  female: '#ec4899',
  f: '#ec4899',
  unknown: '#bdbdbd',
};

const TISSUE_COLORS: Record<string, string> = {
  colon: '#7c6aa6',
  ileum: '#f0a202',
};

type PcaPlotProps = {
  points: GeoBrowsePcaPoint[];
  samples?: GeoBrowseSample[];
};

type PcaDatum = {
  x: number;
  y: number;
  id: string;
  series: string;
};

function getSampleColorValue(sample: GeoBrowseSample | undefined, point: GeoBrowsePcaPoint, colorBy: PcaColorBy): string {
  if (!sample) {
    if (colorBy === 'group') return point.group.toLowerCase();
    return 'unknown';
  }
  if (colorBy === 'group') return (sample.group || point.group).toLowerCase();
  if (colorBy === 'sex') return (sample.sex || 'unknown').toLowerCase();
  return (sample.tissue || 'unknown').toLowerCase();
}

function colorForCategory(colorBy: PcaColorBy, category: string): string {
  const key = category.toLowerCase();
  if (colorBy === 'group') return GROUP_COLORS[key] ?? '#718096';
  if (colorBy === 'sex') return SEX_COLORS[key] ?? '#bdbdbd';
  return TISSUE_COLORS[key] ?? '#718096';
}

function getAvailableColorBy(samples: GeoBrowseSample[]): PcaColorBy[] {
  const options: PcaColorBy[] = ['group'];
  if (samples.some(s => s.sex && s.sex.toLowerCase() !== 'unknown')) options.push('sex');
  if (samples.some(s => s.tissue)) options.push('tissue');
  return options;
}

export default function PcaPlot({ points, samples = [] }: PcaPlotProps) {
  const sampleMap = useMemo(
    () => new Map(samples.map(s => [s.sample_id, s])),
    [samples],
  );

  const colorByOptions = useMemo(() => getAvailableColorBy(samples), [samples]);
  const [colorBy, setColorBy] = useState<PcaColorBy>('group');

  const activeColorBy = colorByOptions.includes(colorBy) ? colorBy : 'group';

  const pctVarPc1 = points[0]?.pct_var_pc1 ?? 0;
  const pctVarPc2 = points[0]?.pct_var_pc2 ?? 0;

  const series = useMemo(() => {
    const grouped = new Map<string, PcaDatum[]>();

    for (const p of points) {
      const sample = sampleMap.get(p.sample_id);
      const category = getSampleColorValue(sample, p, activeColorBy);
      const bucket = grouped.get(category) ?? [];
      bucket.push({ x: p.pc1, y: p.pc2, id: p.sample_id, series: category });
      grouped.set(category, bucket);
    }

    return [...grouped.entries()].map(([id, data]) => ({ id, data }));
  }, [points, sampleMap, activeColorBy]);

  const seriesColors = useMemo(
    () => series.map(s => colorForCategory(activeColorBy, String(s.id))),
    [series, activeColorBy],
  );

  if (!points.length) {
    return <div className={styles.tabEmpty}>No PCA data available for this study.</div>;
  }

  return (
    <>
      <div className={styles.chartToolbar}>
        <label className={styles.chartToolbarLabel}>
          Color by:
          <select
            className={styles.chartToolbarSelect}
            value={activeColorBy}
            onChange={e => setColorBy(e.target.value as PcaColorBy)}
          >
            {colorByOptions.map(option => (
              <option key={option} value={option}>
                {COLOR_BY_LABELS[option]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className={styles.chartArea}>
        <ResponsiveScatterPlot<PcaDatum>
          key={`${activeColorBy}-${series.map(s => s.id).join('|')}`}
          data={series}
          margin={{ top: 24, right: 120, bottom: 52, left: 60 }}
          xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          xFormat=" >-.2f"
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          yFormat=" >-.2f"
          axisBottom={{
            legend: `PC1 (${pctVarPc1.toFixed(1)}% var)`,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            legend: `PC2 (${pctVarPc2.toFixed(1)}% var)`,
            legendOffset: -44,
            legendPosition: 'middle',
          }}
          colors={seriesColors}
          nodeSize={8}
          theme={CHART_THEME}
          layers={[
            'grid',
            'axes',
            (props: ScatterPlotCustomSvgLayerProps<PcaDatum>) => (
              <g>
                {props.nodes.map(node => (
                  <circle
                    key={String(node.id)}
                    cx={node.x}
                    cy={node.y}
                    r={4}
                    fill={colorForCategory(activeColorBy, node.data.series)}
                  />
                ))}
              </g>
            ),
            'mesh',
            'legends',
          ]}
          legends={[
            {
              anchor: 'top-right',
              direction: 'column',
              translateX: 110,
              itemWidth: 100,
              itemHeight: 20,
              symbolSize: 10,
              symbolShape: 'circle',
            },
          ]}
          tooltip={({ node }) => {
            const sample = sampleMap.get(String(node.data.id));
            return (
              <div className={styles.volcanoTooltip}>
                <strong>{node.data.id}</strong>
                <div>PC1: {Number(node.data.x).toFixed(2)}</div>
                <div>PC2: {Number(node.data.y).toFixed(2)}</div>
                {sample && (
                  <>
                    <div>group: {sample.group}</div>
                    <div>sex: {sample.sex}</div>
                    {sample.tissue && <div>tissue: {sample.tissue}</div>}
                  </>
                )}
              </div>
            );
          }}
        />
      </div>
    </>
  );
}
